import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('ProfileSlugService', 'profile-management');

/**
 * Service for managing profile URL slugs
 * Handles generation, validation, and reservation of URL-safe slugs for loan officer profiles
 */
export class ProfileSlugService {
  /**
   * Generate a URL-safe slug from first and last name
   * @param firstName - User's first name
   * @param lastName - User's last name
   * @returns URL-safe slug
   */
  private static generateBaseSlug(firstName: string, lastName: string): string {
    if (!firstName || !lastName) {
      throw new Error('First name and last name are required');
    }

    return `${firstName}-${lastName}`
      .toLowerCase()
      .normalize('NFD') // Normalize unicode characters
      .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
      .replace(/[^a-z0-9-]/g, '-') // Replace non-alphanumeric with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single
      .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
  }

  /**
   * Check if a slug is available
   * @param slug - The slug to check
   * @param excludeUserId - Optional user ID to exclude from check (for updates)
   * @returns True if available, false otherwise
   */
  private static async isSlugAvailable(slug: string, excludeUserId?: string): Promise<boolean> {
    try {
      // Check in users table directly
      let query = supabase
        .from('users')
        .select('id')
        .eq('website_slug', slug);
      
      // Exclude current user when checking for updates
      if (excludeUserId) {
        query = query.neq('id', excludeUserId);
      }
      
      const { data, error } = await query.maybeSingle();

      // If error (other than no rows found) throw it
      if (error && error.code !== 'PGRST116') {
        log.error('Error checking slug availability', { slug }, error);
        throw error;
      }

      // Slug is available if no data found
      return !data;
    } catch (error) {
      log.error('Failed to check slug availability', { slug }, error as Error);
      throw error;
    }
  }

  /**
   * Generate a unique slug for a loan officer
   * @param userId - The user's ID
   * @param firstName - User's first name
   * @param lastName - User's last name
   * @returns Unique URL slug
   */
  static async generateUniqueSlug(
    userId: string,
    firstName: string,
    lastName: string
  ): Promise<string> {
    try {
      const baseSlug = this.generateBaseSlug(firstName, lastName);
      
      // Check if base slug is available (exclude current user to allow keeping their own slug)
      if (await this.isSlugAvailable(baseSlug, userId)) {
        log.info('Generated unique slug', { userId, slug: baseSlug });
        return baseSlug;
      }

      // Try with incrementing numbers
      for (let i = 2; i <= 999; i++) {
        const numberedSlug = `${baseSlug}-${i}`;
        if (await this.isSlugAvailable(numberedSlug, userId)) {
          log.info('Generated unique slug with number', { userId, slug: numberedSlug });
          return numberedSlug;
        }
      }

      // Fallback to UUID suffix if somehow 999 variations exist
      const uuidSuffix = userId.split('-')[0];
      const uuidSlug = `${baseSlug}-${uuidSuffix}`;
      log.warn('Using UUID suffix for slug', { userId, slug: uuidSlug });
      return uuidSlug;
    } catch (error) {
      log.error('Failed to generate unique slug', { userId, firstName, lastName }, error as Error);
      throw error;
    }
  }

  /**
   * Reserve a slug for a user
   * Updates the user's website_slug in the users table
   * @param userId - The user's ID
   * @param slug - The slug to reserve
   */
  static async reserveSlug(userId: string, slug: string): Promise<void> {
    try {
      // First check if we have an authenticated session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        log.error('No authenticated session when reserving slug', { userId, sessionError });
        throw new Error('You must be logged in to reserve a profile URL');
      }
      
      log.info('Reserving slug with session', { 
        userId, 
        sessionUserId: session.user.id, 
        slug,
        userIdMatchesSession: userId === session.user.id 
      });
      
      // Check if slug is available (excluding current user)
      const isAvailable = await this.isSlugAvailable(slug, userId);
      if (!isAvailable) {
        throw new Error('This URL is already taken. Please try a different name.');
      }

      // Reserve only. Publishing is a separate, readiness-gated action.
      const { error: updateError } = await supabase
        .from('users')
        .update({ website_slug: slug })
        .eq('id', userId);  // Use 'id' not 'user_id' as users table has 'id' column

      if (updateError) {
        log.error('Failed to update user profile with slug', { userId, slug }, updateError);
        throw updateError;
      }

      // Optionally, also update profile_url_slugs table for backward compatibility
      // But don't fail if this doesn't work since it's not critical
      try {
        // First deactivate any existing slugs for this user
        await supabase
          .from('profile_url_slugs')
          .update({ is_active: false })
          .eq('user_id', userId)
          .eq('is_active', true);

        // Then insert new slug (ignore errors as this is optional)
        await supabase
          .from('profile_url_slugs')
          .insert({
            user_id: userId,
            slug,
            is_active: true
          });
      } catch (slugTableError) {
        // Log but don't throw - the main users table update succeeded
        log.warn('Could not update profile_url_slugs table (non-critical)', { userId, slug }, slugTableError as Error);
      }

      log.info('Successfully reserved slug', { userId, slug });
    } catch (error) {
      log.error('Failed to reserve slug', { userId, slug }, error as Error);
      throw error;
    }
  }

  /**
   * Get the profile URL for a slug (for display purposes)
   * Uses the current window origin - suitable for UI display
   * @param slug - The URL slug
   * @returns Full profile URL based on current origin
   */
  static getProfileUrl(slug: string): string {
    // Always use the current origin for displaying URLs
    // This ensures the URL matches where the user is accessing from
    const baseUrl = window.location.origin;
    return `${baseUrl}/p/${slug}`;
  }

  /**
   * Get the shareable profile URL for a slug (for external sharing).
   *
   * Public profile links live on the consumer site, not the portal. This is
   * the officer's permanent canonical page. It is only correct to hand out
   * once the published site acknowledges delivery — callers that display or
   * copy a link must go through the delivery state, which falls back to
   * {@link getDirectoryAnchorUrl} while a page is pending.
   *
   * @param slug - The URL slug
   * @returns Full public profile URL on lendwisemtg.com
   */
  static getShareableUrl(slug: string): string {
    return `https://lendwisemtg.com/loan-officers/${slug}`;
  }

  /**
   * The always-resolving fallback link: the public directory with the
   * officer's card anchor. Used until the per-officer page is delivered.
   * @param slug - The URL slug
   */
  static getDirectoryAnchorUrl(slug: string): string {
    return `https://lendwisemtg.com/loan-officers#${slug}`;
  }

  /**
   * Decide how the Generate URL action obtains a slug. Slugs are permanent
   * once created — regenerating after a name change strands every previously
   * shared link (profile_url_slugs keeps no usable history) — so an existing
   * slug is always reused and generation is only reachable when none exists.
   * @param existingSlug - The slug currently on the profile/form, if any
   * @returns reuse (with the slug) or generate
   */
  static decideWebsiteSlugGeneration(
    existingSlug: string | null | undefined
  ): { action: 'reuse'; slug: string } | { action: 'generate' } {
    const slug = typeof existingSlug === 'string' ? existingSlug.trim() : '';
    return slug ? { action: 'reuse', slug } : { action: 'generate' };
  }

  /**
   * Get the preview URL for a slug
   * @param slug - The URL slug
   * @returns Full preview URL
   */
  static getPreviewUrl(slug: string): string {
    // Always use the current origin for displaying URLs
    const baseUrl = window.location.origin;
    return `${baseUrl}/preview/p/${slug}`;
  }

  /**
   * Get user's current active slug
   * @param userId - The user's ID
   * @returns The active slug or null
   */
  static async getUserSlug(userId: string): Promise<string | null> {
    try {
      // First try to get from users table (primary source)
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('website_slug')
        .eq('id', userId)
        .single();

      if (!userError && userData?.website_slug) {
        return userData.website_slug;
      }

      // Fallback to profile_url_slugs table for backward compatibility
      const { data, error } = await supabase
        .from('profile_url_slugs')
        .select('slug')
        .eq('user_id', userId)
        .eq('is_active', true)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        log.error('Error fetching user slug from profile_url_slugs', { userId }, error);
        // Don't throw, just return null
      }

      return data?.slug || null;
    } catch (error) {
      log.error('Failed to get user slug', { userId }, error as Error);
      throw error;
    }
  }

  /**
   * Release a user's slug (deactivate it)
   * @param userId - The user's ID
   */
  static async releaseSlug(userId: string): Promise<void> {
    try {
      // Clear slug from users table (primary)
      const { error: userError } = await supabase
        .from('users')
        .update({ 
          website_slug: null,
          website_enabled: false
        })
        .eq('id', userId);  // Use 'id' not 'user_id'

      if (userError) {
        log.error('Failed to clear slug from users table', { userId }, userError);
        throw userError;
      }

      // Also try to deactivate in profile_url_slugs table (optional)
      try {
        await supabase
          .from('profile_url_slugs')
          .update({ is_active: false })
          .eq('user_id', userId)
          .eq('is_active', true);
      } catch (slugTableError) {
        // Log but don't throw - the main users table update succeeded
        log.warn('Could not update profile_url_slugs table (non-critical)', { userId }, slugTableError as Error);
      }

      log.info('Successfully released slug', { userId });
    } catch (error) {
      log.error('Failed to release slug', { userId }, error as Error);
      throw error;
    }
  }

  /**
   * Validate if a slug meets requirements
   * @param slug - The slug to validate
   * @returns True if valid, false otherwise
   */
  static isValidSlug(slug: string): boolean {
    // Must be 3-50 characters, start and end with alphanumeric, can contain hyphens
    const slugRegex = /^[a-z0-9][a-z0-9-]{1,48}[a-z0-9]$/;
    return slugRegex.test(slug);
  }
}
